import React from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// What a keyword actually targets, by NAME. Used by both keyword tables so the
// two tools read identically.
//
// Counts ("2 groups + 5 pages") were useless at a glance — the point of the
// column is knowing WHO is being watched. Names come resolved from the backend,
// so no table has to fetch the advertiser list to draw a row.

interface AdvertiserTargetsProps {
  groups: Array<{ groupId: string; groupName: string }> | null;
  pages: Array<{ pageId: string; pageName: string }> | null;
  /** Show at most this many before collapsing the rest into a +N chip. */
  max?: number;
}

export default function AdvertiserTargets({ groups, pages, max = 3 }: AdvertiserTargetsProps) {
  const all = [
    ...(groups || []).map(g => ({ key: `g:${g.groupId}`, name: g.groupName, isGroup: true })),
    ...(pages || []).map(p => ({ key: `p:${p.pageId}`, name: p.pageName, isGroup: false })),
  ];

  if (!all.length) {
    // Said plainly rather than left blank: "no filter" and "a filter that
    // matched nothing" look identical in a result count otherwise.
    return <Typography variant="caption" color="text.disabled">All advertisers</Typography>;
  }

  const shown = all.slice(0, max);
  const hidden = all.slice(max);

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {shown.map(item => (
        <Chip
          key={item.key}
          size="small"
          // Groups are filled, individual pages outlined — a group is a live
          // reference whose membership can change, a page is exactly itself.
          variant={item.isGroup ? 'filled' : 'outlined'}
          color={item.isGroup ? 'primary' : 'default'}
          label={item.name}
          sx={{ height: 20, fontSize: '0.65rem', maxWidth: 160 }}
        />
      ))}
      {!!hidden.length && (
        <Tooltip title={hidden.map(h => h.name).join(', ')}>
          <Chip size="small" label={`+${hidden.length}`} sx={{ height: 20, fontSize: '0.65rem' }} />
        </Tooltip>
      )}
    </Stack>
  );
}
